import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Empty, Select, Spin, TreeSelect } from 'antd'
import type { SelectProps } from 'antd'
import { debounce, isEmpty } from 'lodash-es'

export interface DebounceSelectProps<ValueType = any>
  extends Omit<SelectProps<ValueType | ValueType[]>, 'options' | 'children'> {
  fetchOptions: (search: Record<string, any>) => Promise<ValueType[]>
  debounceTimeout?: number
  label?: string // 搜索框 提示文案：请搜索选择label
  isCommon?: boolean
  readOnly?: boolean
  enabled?: boolean | null
  nameKey?: string // 搜索Key
  query?: Record<string, any> // 回显查询参数
  fixedParams?: Record<string, any> // 其他固定查询参数
  resDataIsArray?: boolean // 返回数据是否是数组
  resetFetch?: boolean // 是否重置查询
  cancelInitFetch?: boolean // 是否取消初始化查询
}

export default forwardRef(function DebounceSelect<
  ValueType extends { key?: string; label: React.ReactNode } = any,
>(
  {
    fetchOptions,
    debounceTimeout = 800,
    label,
    query = {},
    fixedParams = {},
    nameKey = 'nameKey',
    fieldNames,
    resDataIsArray,
    resetFetch,
    cancelInitFetch,
    ...props
  }: DebounceSelectProps<ValueType>,
  ref
) {
  const [fetching, setFetching] = useState(false)
  const [isReload, setIsReload] = useState(false)
  const [options, setOptions] = useState<any>([])
  const [value, setValue] = useState<any>(undefined)
  const [params] = useState(query)
  useEffect(() => {
    setValue(props.value)
  }, [props.value])
  useEffect(() => {
    if (!isEmpty(value) && query !== params) {
      getData()
    }
  }, [query])

  useEffect(() => {
    !cancelInitFetch && getData()
  }, [])
  useImperativeHandle(
    ref,
    () => ({
      reload: params => {
        getData('', true, params)
      },
    }),
    []
  )

  useEffect(() => {
    isReload && getData('', isReload)
  }, [isReload])

  const getData = (value?, isReload = false, reloadSearchParmas?: any): Promise<any> => {
    const rest =
      value || isReload ? { ...fixedParams, ...reloadSearchParmas } : { ...query, ...fixedParams }
    return fetchOptions({ pageId: 1, pageSize: 10, [nameKey]: value || undefined, ...rest })
      .then((res: any) => {
        if (resDataIsArray && res?.length) {
          return res
            .map(item => {
              if (typeof item === 'string') {
                return {
                  label: item,
                  value: item,
                }
              } else {
                return {
                  ...item,
                  label: item[fieldNames?.label || 'name'],
                  value: item[fieldNames?.value || 'id'],
                }
              }
            })
            ?.filter(item => item.value)
        }
        const data = res.list?.map(item => ({
          ...item,
          label: item[fieldNames?.label || 'name'],
          value: item[fieldNames?.value || 'id'],
        }))
        return data
      })
      .then(newOptions => {
        setOptions(newOptions)
        setFetching(false)
      })
  }

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => {
      setOptions([])
      setFetching(true)
      getData(value)
    }
    return debounce(loadOptions, debounceTimeout)
  }, [debounceTimeout, fixedParams])

  const onChange = (v, option) => {
    props.onChange?.(props.mode ? v?.map(item => item.value) : v?.value, option)
    if (!v) {
      setIsReload(true)
      setValue(undefined)
    }
    if (props.mode && v.length <= 0) getData()
  }
  return (
    <>
      {props?.readOnly ? (
        <div>{options.find(item => item.value === props.value)?.label}</div>
      ) : (
        <Select
          {...props}
          key={props.id}
          labelInValue
          allowClear
          showSearch
          value={value}
          placeholder={label}
          filterOption={false}
          onSearch={debounceFetcher}
          notFoundContent={
            fetching ? (
              <Spin size="small" />
            ) : (
              <div>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              </div>
            )
          }
          onChange={onChange}
          onBlur={() => {
            return getData('', isReload)
          }}
          options={options}
        />
      )}
    </>
  )
})
